import { Link, NavLink, Outlet } from "react-router-dom";

import { useAuth } from "@/customer/auth/AuthContext";
import { cn } from "@/lib/utils";

const TABS: Array<{ to: string; label: string; end?: boolean }> = [
  { to: "/account", label: "Profile", end: true },
  { to: "/account/addresses", label: "Addresses" },
  { to: "/account/security", label: "Security" },
  { to: "/account/coi", label: "Insurance" }
];

// Phase 2a customer-accounts FR-7 — the signed-in customer's account
// area. The Orders tab sketched in FR-7 lands alongside the customer
// portal (4a) slice. COI is wired (2b); orders stays out of nav v1 so
// signing in doesn't surface "coming soon" placeholders.
export function AccountPage(): JSX.Element {
  const auth = useAuth();

  if (!auth.user) {
    // Not signed in. Show a sign-in prompt rather than redirecting so a
    // user who lands here via bookmark sees a meaningful page.
    return (
      <section className="mx-auto max-w-md space-y-4 rounded-md border border-zinc-200 bg-brand-white p-6 text-center">
        <h1 className="text-xl font-semibold text-brand-black">Sign in to manage your account</h1>
        <Link to="/sign-in?next=/account" className="inline-block text-brand-red underline">
          Go to sign in
        </Link>
      </section>
    );
  }

  return (
    <section className="space-y-4">
      <header className="flex flex-wrap items-baseline justify-between gap-3 border-b border-zinc-200 pb-4">
        <div>
          <h1 className="text-2xl font-semibold text-brand-black">Your account</h1>
          <p className="text-sm text-zinc-600">Signed in as {auth.user.email}</p>
        </div>
      </header>

      <nav aria-label="Account sections" className="border-b border-zinc-200">
        <ul className="flex gap-1">
          {TABS.map((t) => (
            <li key={t.to}>
              <NavLink
                to={t.to}
                end={t.end ?? false}
                className={({ isActive }) =>
                  cn(
                    "inline-block border-b-2 px-4 py-2 text-sm font-medium",
                    isActive
                      ? "border-brand-red text-brand-black"
                      : "border-transparent text-zinc-600 hover:text-brand-black"
                  )
                }
              >
                {t.label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      <div className="pt-2">
        <Outlet />
      </div>
    </section>
  );
}
